<script setup>

import { ref, watch, onUnmounted, useTemplateRef } from 'vue';
import { Spinner, TextInput, Button } from '@cloudron/pankow';
import MainModel from '../models/MainModel.js';
import { getFilesViewHashHref, getResultLocationLabel, getResultFolderPath } from '../utils.js';
import { followLink, go } from '../router.js';

const SEARCH_DEBOUNCE_MS = 500;

const searchResultsPanel = useTemplateRef('searchResultsPanel');
const searchBar = useTemplateRef('searchBar');
const searchInput = useTemplateRef('searchInput');

const searchPending = ref(0);
const dismissed = ref(false);
const searchQuery = ref('');
const searchResults = ref([]);
const resultsOpen = ref(false);
const open = ref(false);

let debounceTimer = null;
let searchAbortController = null;
let searchGeneration = 0;

function clearDebounceTimer() {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}

function abortInFlightSearch() {
  searchAbortController?.abort();
  searchAbortController = null;
  searchGeneration++;
}

async function runSearch() {
  const q = searchQuery.value.trim();
  if (!q) return;

  searchAbortController?.abort();
  const controller = new AbortController();
  searchAbortController = controller;

  const gen = ++searchGeneration;
  searchPending.value++;

  let results;
  try {
    results = await MainModel.search('*' + q + '*', { signal: controller.signal });
  } catch (e) {
    if (e?.name === 'AbortError') return;
    throw e;
  } finally {
    searchPending.value--;
  }

  if (gen !== searchGeneration || dismissed.value) return;

  results.sort((a, b) => {
    return new Date(b.entry.mtime).getTime() - new Date(a.entry.mtime).getTime();
  });

  searchResults.value = results;
  resultsOpen.value = true;
}

function scheduleDebouncedSearch() {
  clearDebounceTimer();
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    runSearch();
  }, SEARCH_DEBOUNCE_MS);
}

function onMagnifierClick() {
  searchInput.value?.focus();
}

function onOpenResults() {
  const q = searchQuery.value.trim();
  if (!q) return;

  go('/search/' + encodeURIComponent(q));
  onDismiss();
}

function onResultLink(event, href) {
  followLink(event, href);
  if (event.defaultPrevented) onDismiss();
}

watch(searchQuery, () => {
  const q = searchQuery.value.trim();
  if (!q) {
    clearDebounceTimer();
    abortInFlightSearch();
    searchResults.value = [];
    resultsOpen.value = false;
    return;
  }
  if (!open.value || dismissed.value) return;
  scheduleDebouncedSearch();
});

onUnmounted(() => {
  clearDebounceTimer();
  abortInFlightSearch();
});

function onDismiss() {
  open.value = false;
  resultsOpen.value = false;

  clearDebounceTimer();
  abortInFlightSearch();

  searchQuery.value = '';
  dismissed.value = true;

  setTimeout(() => { searchResults.value = []; }, 1000);
}

function onFocus() {
  open.value = true;
  dismissed.value = false;
  searchResultsPanel.value.style.width = searchBar.value.offsetWidth + 'px';
}

function getEntryHref(entry) {
  return getFilesViewHashHref(entry, entry.filePath);
}

function getDirectoryHref(entry) {
  const folderPath = entry.isDirectory ? entry.filePath : entry.filePath.slice(0, -entry.fileName.length);
  return getFilesViewHashHref(entry, folderPath);
}

</script>

<template>
  <div class="search-bar-container" @keydown.esc="onDismiss" tabindex="1">
    <Transition name="pankow-fade">
      <div class="pankow-dialog-backdrop" @click="onDismiss" v-show="open"></div>
    </Transition>
    <div class="search-bar" ref="searchBar" :class="{'open': open,'results-open': resultsOpen}">
      <i class="fa-solid fa-magnifying-glass" v-show="searchPending === 0" @click="onMagnifierClick"></i>
      <Spinner v-show="searchPending > 0" />
      <TextInput ref="searchInput" v-model="searchQuery" placeholder="Search ..." @focus="onFocus" @click="onFocus" @keydown.enter.prevent="onOpenResults" class="search-input"/>
    </div>
    <Transition name="pankow-roll-down">
      <div v-show="resultsOpen" ref="searchResultsPanel" class="search-result-panel">
        <div v-show="searchResults.length === 0" class="no-search-results">
          Nothing found
        </div>
        <a v-for="result in searchResults" :key="result.entry.id" class="search-result-entry" :href="getEntryHref(result.entry)" @click.stop="onResultLink($event, getEntryHref(result.entry))">
          <img :src="result.entry.previewUrl"/>
          <div class="search-result-body">
            <div class="search-result-title"><b>{{ result.fileName }}</b></div>
            <div class="search-result-path-row">
              <span class="search-result-location" :title="getResultLocationLabel(result.entry)">{{ getResultLocationLabel(result.entry) }}</span>
              <span class="search-result-folder-path">{{ getResultFolderPath(result.entry, result.fileName) }}</span>
            </div>
          </div>
          <div class="search-result-entry-actions">
            <Button plain :href="getDirectoryHref(result.entry)" @click.stop="onResultLink($event, getDirectoryHref(result.entry))">Open folder</Button>
          </div>
        </a>
      </div>
    </Transition>
  </div>
</template>

<style scoped>

.search-bar-container {
  padding-left: 10px;
  padding-right: 10px;
  display: flex;
  flex-grow: 1;
}

/* do not cover the main sidebar toggle */
@media (max-width: 576px) {
  .search-bar-container {
    padding-left: 40px;
  }
}

.search-bar {
  padding-left: 10px;
  border-radius: var(--pankow-border-radius);
  display: flex;
  align-items: center;
  flex-grow: 1;
  position: relative;
}

.open {
  z-index: 3001;
  background: var(--pankow-dialog-background-color);
}

.search-bar.results-open {
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

.search-input {
  border: none;
  background: transparent;
  flex-grow: 1;
}

.search-result-entry {
  display: flex;
  align-items: start;
  cursor: pointer;
  padding: 10px 5px;
  align-items: center;
}

.search-result-entry:hover {
  background-color: var(--pankow-color-background-hover);
}

.search-result-entry > img {
  height: 60px;
  width: 60px;
  object-fit: cover;
}

.search-result-body {
  margin-left: 10px;
  flex-grow: 1;
  min-width: 0;
}

.search-result-title {
  margin-bottom: 10px;
}

.search-result-path-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
  min-width: 0;
}

.search-result-location {
  flex-shrink: 0;
  max-width: 45%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
}

.search-result-folder-path {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-result-entry-actions {
  visibility: hidden;
  margin-right: 20px;
}

.search-result-entry:hover .search-result-entry-actions {
  visibility: visible;
}

.search-result-panel {
  border-bottom-left-radius: var(--pankow-border-radius);
  border-bottom-right-radius: var(--pankow-border-radius);
  position: absolute;
  background: var(--pankow-dialog-background-color);
  top: 35px;
  z-index: 3000;
  box-shadow: var(--pankow-menu-shadow);
  overflow: scroll;
  max-height: min(70vh, 512px);
}

.no-search-results {
  text-align: center;
  padding-bottom: 20px;
}

</style>
